import {
  FilterSelections,
  FTSVariables,
  SearchObjectsModel,
  SearchTerms,
  FilterSelection,
  SearchArguments
} from '../../../../../types/Search';
import {Maybe} from "../../../../../types/Maybe";
import {AggregationType} from "../../../../../types/AggregationType";
import {SearchQueryArgumentBuilder} from "./search-query-argument-builder";
import {gql} from "apollo-boost";
import VariableOptions from "gql-query-builder/build/VariableOptions";
import {QueryBuilder} from "../../query-builder";
import {CONCEPT, FROM, SIZE, TERMS} from "../variables";

const OPERATION_NAME = 'ConceptSearch';
const DEFAULT_QUERY_FIELDS = ['id', 'name'];
const FILTER_VARIABLE_PREFIX = 'filter_';

export class SearchQueryBuilder {

  private readonly searchArguments: SearchArguments;
  private readonly searchObjectsModel: SearchObjectsModel;
  private includeAggregations: boolean;

  constructor(searchArguments: SearchArguments, searchObjectsModel: SearchObjectsModel) {
    this.searchArguments = searchArguments;
    this.searchObjectsModel = searchObjectsModel;
    this.includeAggregations = true;
  }

  withoutAggregations(): SearchQueryBuilder {
    this.includeAggregations = false;
    return this;
  }

  build() {
    const searchTerms: SearchTerms = this.searchArguments.searchTerms;
    const selections: FilterSelections = this.searchArguments.filterSelection;

    const searchArguments = new SearchQueryArgumentBuilder()
      .setSearchTerms(searchTerms)
      .setSearchableObjects(this.searchObjectsModel.objectsMap)
      .setSelections(selections)
      .setIncludeAggregations(this.includeAggregations)
      .build();

    const queryOptions = QueryBuilder.buildQueryOptions({
      params: searchArguments,
      fields: DEFAULT_QUERY_FIELDS,
      operationName: OPERATION_NAME,
      variables: this.buildVariableOptions()
    });

    return {
      query: gql`${queryOptions.query}`,
      variables: SearchQueryBuilder.buildSearchVariables(searchTerms, this.searchArguments.from,
        this.searchArguments.size)
    };
  }

  private buildVariableOptions(): VariableOptions {
    const variables: VariableOptions = {};
    variables[SIZE] = {required: true, type: 'Int'};
    variables[FROM] = {required: true, type: 'Int'};

    if (this.searchArguments.searchTerms.concept?.id) {
      variables[CONCEPT] = {required: true};
    }

    this.searchArguments.searchTerms.freeText.forEach((term, index) => {
      variables[`${TERMS}${index}`] = {required: true};
    });

    SearchQueryBuilder.applyOnFilterSelection(this.searchArguments.searchTerms.filters,
      (filter: FilterSelection) => {
        if (filter.facetType === AggregationType.DATERANGE) {
          variables[`${FILTER_VARIABLE_PREFIX}${filter.field}_start`] = {required: true};
          variables[`${FILTER_VARIABLE_PREFIX}${filter.field}_end`] = {required: true};
        } else if (filter.facetType === AggregationType.NUMERIC_RANGE) {
          variables[`${FILTER_VARIABLE_PREFIX}${filter.field}_min`] = {required: true};
          variables[`${FILTER_VARIABLE_PREFIX}${filter.field}_max`] = {required: true};
        } else if (filter.facetType === AggregationType.KEYWORD) {
          variables[`${FILTER_VARIABLE_PREFIX}${filter.field}`] = {required: true};
        }
      });

    return variables;
  }

  private static buildSearchVariables(searchTerms: SearchTerms, from: number, size: number): FTSVariables {
    const {concept, freeText, filters = {}} = searchTerms;
    const flatTerms: Record<string, any> = {};
    freeText.forEach((term: string, index: number) => flatTerms[`${TERMS}${index}`] = term);
    const flatFilters: Record<string, any> = {};
    SearchQueryBuilder.applyOnFilterSelection(filters, (filter: FilterSelection) => {
      if (filter.facetType === AggregationType.DATERANGE) {
        flatFilters[`${FILTER_VARIABLE_PREFIX}${filter.field}_start`] = filter.value.startDate;
        flatFilters[`${FILTER_VARIABLE_PREFIX}${filter.field}_end`] = filter.value.endDate;
      } else if (filter.facetType === AggregationType.NUMERIC_RANGE) {
        flatFilters[`${FILTER_VARIABLE_PREFIX}${filter.field}_min`] = filter.value.min;
        flatFilters[`${FILTER_VARIABLE_PREFIX}${filter.field}_max`] = filter.value.max;
      } else if (filter.facetType === AggregationType.KEYWORD) {
        flatFilters[`${FILTER_VARIABLE_PREFIX}${filter.field}`] = filter.value;
      }
    });
    return {concept: concept?.id, ...flatTerms, from, size, ...flatFilters};
  }

  private static applyOnFilterSelection(filters: Maybe<FilterSelections>, callback: (filter: FilterSelection) => void) {
    const _filters = filters || {};
    Object.keys(_filters).forEach((key: string) => {
      callback(_filters[key]);
    });
  }
}
